import React, { Suspense } from 'react';
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
} from 'react-router-dom';
import { paths } from 'tidee-life-core';
import Loading from 'tidee-life-ui/dist/src/loading.js';
import Error404 from '../components/error-404/error-404.tsx';
import ErrorBoundary from '../components/error-boundary/error-boundary.tsx';
import { lazyWithRecovery as lazy } from './lazy-with-recovery.ts';

const AppWrapperNotLoggedIn = lazy(
    () =>
        import(
            '../components/layout/app-wrapper-not-logged-in/app-wrapper-not-logged-in.tsx'
        )
);

const AppWrapperLoggedInProviders = lazy(
    () =>
        import(
            '../components/layout/app-wrapper-logged-in/app-wrapper-logged-in-providers.tsx'
        )
);

const LogOut = lazy(() => import('../components/logout/log-out.tsx'));

const LoginPageLoader = lazy(() => import('../pages/login/login.tsx'));

const SignUpPageLoader = lazy(() => import('../pages/sign-up/sign-up.tsx'));

const SignUpSuccessPageLoader = lazy(
    () => import('../pages/sign-up-success/sign-up-success.tsx')
);

const ForgotPageLoader = lazy(() => import('../pages/forgot/forgot.tsx'));

const ForgotSuccessPageLoader = lazy(
    () => import('../pages/forgot-success/forgot-success.tsx')
);

const PasswordResetPageLoader = lazy(
    () => import('../pages/password-reset/password-reset.tsx')
);

const PasswordResetSuccessPageLoader = lazy(
    () => import('../pages/password-reset-success/password-reset-success.tsx')
);

const ActivateAccountPageLoader = lazy(
    () => import('../pages/activate-account/activate-account.tsx')
);

const ResendActivationLinkPageLoader = lazy(
    () => import('../pages/resend-activation-link/resend-activation-link.tsx')
);

const ResendActivationLinkSuccessPageLoader = lazy(
    () =>
        import(
            '../pages/resend-activation-link-success/resend-activation-link-success.tsx'
        )
);

const BoxesLoader = lazy(() => import('../pages/items/items.tsx'));

const PrioritizedLoader = lazy(
    () => import('../pages/prioritized/prioritized.tsx')
);

const BacklogLoader = lazy(() => import('../pages/backlog/backlog.tsx'));

const SharesLoader = lazy(() => import('../pages/shares/shares.tsx'));

const IndexRedirect = lazy(
    () => import('../components/layout/index-redirect.tsx')
);

// Phase 4 slice F2b — the OAuth consent screen. Under the logged-in
// wrapper so a session is enforced; the wrapper carries ?next= through
// login, so arriving here signed out returns here afterwards rather
// than dropping the user at the workspace mid-flow.
const AuthorizePageLoader = lazy(
    () => import('../pages/authorize/authorize.tsx')
);

const VerifyEmailPageLoader = lazy(
    () => import('../pages/verify-email/verify-email.tsx')
);

// Phase 3 slice S — invite landing page. Mounted under the not-
// logged-in wrapper (which doesn't force logout), so the page can
// probe the caller's auth state itself and render one of three
// branches. Same-URL for logged-in / logged-out lets the email
// link work regardless of the caller's session state.
const ShareInvitePageLoader = lazy(
    () => import('../pages/share-invite/share-invite.tsx')
);

const router = createBrowserRouter([
    {
        path: paths.notLoggedInRoot(),
        element: <AppWrapperNotLoggedIn />,
        children: [
            { index: true, element: <Navigate to={paths.login()} replace /> },
            { path: paths.login(), element: <LoginPageLoader /> },
            {
                path: `${paths.signup('')}/:organisationInviteHash?`,
                element: <SignUpPageLoader />,
            },
            {
                path: paths.signupSuccess(),
                element: <SignUpSuccessPageLoader />,
            },
            { path: paths.forgot(), element: <ForgotPageLoader /> },
            {
                path: paths.forgotSuccess(),
                element: <ForgotSuccessPageLoader />,
            },
            {
                path: `${paths.passwordReset()}/:passwordResetHash`,
                element: <PasswordResetPageLoader />,
            },
            {
                path: paths.passwordResetSuccess(),
                element: <PasswordResetSuccessPageLoader />,
            },
            {
                path: `${paths.activateAccount('')}/:confirmHash`,
                element: <ActivateAccountPageLoader />,
            },
            {
                path: paths.resendActivationLinkSuccess(),
                element: <ResendActivationLinkSuccessPageLoader />,
            },
            {
                path: paths.resendActivationLink(),
                element: <ResendActivationLinkPageLoader />,
            },
            {
                path: `${paths.shareInvite('')}/:inviteHash`,
                element: <ShareInvitePageLoader />,
            },
        ],
    },
    {
        path: paths.loggedInRoot(),
        element: <AppWrapperLoggedInProviders />,
        children: [
            { index: true, element: <IndexRedirect /> },
            { path: paths.workspace(), element: <BoxesLoader /> },
            { path: paths.prioritized(), element: <PrioritizedLoader /> },
            { path: paths.backlog(), element: <BacklogLoader /> },
            { path: paths.shares(), element: <SharesLoader /> },
            {
                path: paths.organiser(),
                element: <Navigate to={paths.prioritized()} replace />,
            },
            {
                path: `${paths.verifyEmail('')}/:token`,
                element: <VerifyEmailPageLoader />,
            },
            { path: paths.authorize(), element: <AuthorizePageLoader /> },
            { path: paths.logout(), element: <LogOut /> },
        ],
    },
    { path: '*', element: <Error404 /> },
]);

export const renderRoutes = (): React.ReactElement => {
    console.log('[Routes] Rendering routes');
    return (
        <ErrorBoundary>
            <Suspense fallback={<Loading type="page" />}>
                <RouterProvider router={router} />
            </Suspense>
        </ErrorBoundary>
    );
};
